import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// ─── Individual permission check definitions ──────────────────────────────────
// CHECKS is computed dynamically inside the component based on exam settings
// so camera/mic only show as REQUIRED when AI proctoring is actually enabled

function getChecks(examMeta) {
  const isProctored     = examMeta?.proctoring_enabled !== false;
  const needsFullscreen = examMeta?.require_fullscreen !== false;
  return [
    {
      id: 'camera',
      label: 'Webcam / Camera',
      icon: '📷',
      description: isProctored
        ? 'Required to monitor your face during the exam'
        : 'Camera not required — AI proctoring is disabled for this exam',
      critical: isProctored,
      skip: !isProctored,
    },
    {
      id: 'microphone',
      label: 'Microphone',
      icon: '🎤',
      description: isProctored
        ? 'Required to detect audio during the exam'
        : 'Microphone not required — AI proctoring is disabled for this exam',
      critical: isProctored,
      skip: !isProctored,
    },
    {
      id: 'fullscreen',
      label: 'Fullscreen Mode',
      icon: '⛶',
      description: needsFullscreen
        ? 'Exam must run in fullscreen to prevent tab switching'
        : 'Fullscreen not required for this exam',
      critical: needsFullscreen,
      skip: !needsFullscreen,
    },
    {
      id: 'browser',
      label: 'Browser Compatibility',
      icon: '🌐',
      description: 'Your browser must support all required APIs',
      critical: false,
      skip: false,
    },
    {
      id: 'connection',
      label: 'Network Connection',
      icon: '📡',
      description: 'Stable internet connection required to submit answers',
      critical: false,
      skip: false,
    },
  ];
}

// ─── Error messages mapped from browser error names ───────────────────────────
const CAMERA_ERRORS = {
  NotAllowedError:    'Camera access was denied. Please click the camera icon in your browser address bar and allow access, then refresh.',
  NotFoundError:      'No camera detected on this device. Please connect a webcam and try again.',
  NotReadableError:   'Camera is already in use by another application (e.g. Zoom, Teams). Close that app and try again.',
  OverconstrainedError:'Camera does not meet the required resolution. Try a different camera.',
  SecurityError:      'Camera access blocked by browser security policy. Make sure you are on a secure (HTTPS) connection.',
  AbortError:         'Camera initialisation was interrupted. Please reload the page and try again.',
  default:            'An unknown camera error occurred. Please check your device settings and browser permissions.',
};

const MIC_ERRORS = {
  NotAllowedError:    'Microphone access was denied. Click the microphone icon in your browser address bar, allow access, then refresh.',
  NotFoundError:      'No microphone detected. Please connect a microphone and try again.',
  NotReadableError:   'Microphone is already in use by another application. Close that app and try again.',
  SecurityError:      'Microphone access blocked by browser security policy.',
  default:            'An unknown microphone error occurred. Please check your device settings.',
};

function getErrorMessage(err, map) {
  return map[err?.name] || map.default;
}

// ─── Single check row component ───────────────────────────────────────────────
function CheckRow({ check, status, errorMsg, onRetry }) {
  const statusConfig = {
    idle:    { color: 'var(--text-muted)',      icon: '○', label: 'Waiting' },
    running: { color: 'var(--accent-primary)',   icon: '◌', label: 'Checking...', spin: true },
    pass:    { color: 'var(--accent-success)',   icon: '✓', label: 'Passed' },
    fail:    { color: 'var(--accent-danger)',    icon: '✗', label: 'Failed' },
    warn:    { color: 'var(--accent-warning)',   icon: '⚠', label: 'Warning' },
  };
  const s = statusConfig[status] || statusConfig.idle;

  return (
    <div style={{
      padding: '18px 20px',
      background: status === 'fail' ? 'rgba(239,68,68,0.06)' : status === 'pass' ? 'rgba(16,185,129,0.04)' : 'var(--bg-elevated)',
      border: `1px solid ${status === 'fail' ? 'rgba(239,68,68,0.3)' : status === 'pass' ? 'rgba(16,185,129,0.2)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-sm)',
      marginBottom: 10,
      transition: 'all 0.3s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Icon */}
        <div style={{ fontSize: 22, width: 32, textAlign: 'center', flexShrink: 0 }}>{check.icon}</div>

        {/* Info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{check.label}</span>
            {check.critical && (
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: 'rgba(239,68,68,0.15)', color: 'var(--accent-danger)', fontWeight: 600 }}>
                REQUIRED
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{check.description}</div>
        </div>

        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontSize: 18, color: s.color,
            animation: s.spin ? 'spin 1s linear infinite' : 'none',
          }}>{s.icon}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: s.color, minWidth: 70 }}>{s.label}</span>
          {status === 'fail' && onRetry && (
            <button className="btn btn-secondary btn-sm" onClick={onRetry} style={{ fontSize: 11, padding: '4px 10px' }}>
              Retry
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {status === 'fail' && errorMsg && (
        <div style={{
          marginTop: 12, padding: '10px 14px',
          background: 'rgba(239,68,68,0.1)', borderRadius: 6,
          border: '1px solid rgba(239,68,68,0.2)',
        }}>
          <div style={{ fontSize: 12, color: '#fca5a5', lineHeight: 1.6 }}>
            <strong>Why this failed:</strong> {errorMsg}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Camera preview ───────────────────────────────────────────────────────────
function CameraPreview({ stream }) {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream]);
  if (!stream) return null;
  return (
    <div style={{
      width: '100%', aspectRatio: '16/9', borderRadius: 10, overflow: 'hidden',
      border: '2px solid var(--accent-success)', background: '#000', marginTop: 16,
      position: 'relative',
    }}>
      <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{
        position: 'absolute', top: 8, left: 8, background: 'rgba(16,185,129,0.9)',
        color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
      }}>
        ● LIVE
      </div>
    </div>
  );
}

// ─── Main PermissionCheck page ────────────────────────────────────────────────
export default function PermissionCheckPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { examId, examTitle } = location.state || {};

  const [statuses, setStatuses]   = useState({ camera: 'idle', microphone: 'idle', fullscreen: 'idle', browser: 'idle', connection: 'idle' });
  const [examMeta, setExamMeta]    = useState(null);
  const [errors, setErrors]       = useState({});
  const [cameraStream, setCameraStream] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone]       = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  // Exam start password
  const [pwdRequired, setPwdRequired] = useState(false);
  const [pwdEntry,    setPwdEntry]    = useState('');
  const [pwdError,    setPwdError]    = useState('');
  const [pwdVerified, setPwdVerified] = useState(false);

  // Load exam to check require_fullscreen
  useEffect(() => {
    if (!examId) return;
    import('axios').then(({ default: ax }) => {
      ax.get('/api/exams').then(r => {
        const ex = r.data.find(e => e.id === examId);
        if (ex) {
          setExamMeta(ex);
          if (ex.start_password_enabled) setPwdRequired(true);
        }
      }).catch(() => {});
    });
  }, [examId]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    };
  }, [cameraStream]);

  const setStatus = (id, status) => setStatuses(prev => ({ ...prev, [id]: status }));
  const setError  = (id, msg)    => setErrors(prev => ({ ...prev, [id]: msg }));
  const clearError = (id)        => setErrors(prev => { const n = { ...prev }; delete n[id]; return n; });

  // ── Individual checks ────────────────────────────────────────────────────────

  const checkBrowser = useCallback(async () => {
    setStatus('browser', 'running');
    await new Promise(r => setTimeout(r, 400));
    const issues = [];
    if (!navigator.mediaDevices?.getUserMedia) issues.push('getUserMedia API not supported');
    if (!document.documentElement.requestFullscreen && !document.documentElement.webkitRequestFullscreen)
      issues.push('Fullscreen API not supported');
    if (!window.WebSocket) issues.push('WebSocket not supported');
    if (typeof MediaRecorder === 'undefined') issues.push('MediaRecorder not supported (audio monitoring unavailable)');

    if (issues.length === 0) {
      setStatus('browser', 'pass');
    } else {
      setStatus('browser', 'warn');
      setError('browser', `Some features are limited: ${issues.join('; ')}. Consider using Chrome 90+ or Firefox 88+.`);
    }
  }, []);

  const checkConnection = useCallback(async () => {
    setStatus('connection', 'running');
    const start = Date.now();
    try {
      await fetch('/api/auth/me', { method: 'HEAD', cache: 'no-store' }).catch(() => {});
      const latency = Date.now() - start;
      if (!navigator.onLine) throw new Error('offline');
      if (latency > 3000) {
        setStatus('connection', 'warn');
        setError('connection', `High latency detected (${latency}ms). Your connection is slow — answers may take longer to save.`);
      } else {
        setStatus('connection', 'pass');
      }
    } catch {
      setStatus('connection', 'fail');
      setError('connection', 'No internet connection detected. Please check your network and retry before starting the exam.');
    }
  }, []);

  const checkCamera = useCallback(async () => {
    setStatus('camera', 'running');
    clearError('camera');
    // Stop any existing stream first
    if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
      });
      setCameraStream(stream);
      setStatus('camera', 'pass');
    } catch (err) {
      setStatus('camera', 'fail');
      setError('camera', getErrorMessage(err, CAMERA_ERRORS));
    }
  }, [cameraStream]);

  const checkMicrophone = useCallback(async () => {
    setStatus('microphone', 'running');
    clearError('microphone');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      // Just verify it works then stop mic track (camera keeps running)
      stream.getTracks().forEach(t => t.stop());
      setStatus('microphone', 'pass');
    } catch (err) {
      setStatus('microphone', 'fail');
      setError('microphone', getErrorMessage(err, MIC_ERRORS));
    }
  }, []);

  const checkFullscreen = useCallback(async () => {
    setStatus('fullscreen', 'running');
    await new Promise(r => setTimeout(r, 300));

    // If exam doesn't require fullscreen, auto-pass
    if (examMeta && examMeta.require_fullscreen === false) {
      setStatus('fullscreen', 'pass');
      return;
    }

    const supported = !!(
      document.documentElement.requestFullscreen ||
      document.documentElement.webkitRequestFullscreen ||
      document.documentElement.mozRequestFullScreen ||
      document.documentElement.msRequestFullscreen
    );
    if (supported) {
      setStatus('fullscreen', 'pass');
    } else {
      setStatus('fullscreen', 'fail');
      setError('fullscreen', 'Your browser does not support fullscreen mode. Please use Chrome, Firefox, or Edge.');
    }
  }, [examMeta]);

  // ── Run all checks sequentially ──────────────────────────────────────────────
  const runAllChecks = useCallback(async () => {
    setIsRunning(true);
    setIsDone(false);
    setStatuses({ camera: 'idle', microphone: 'idle', fullscreen: 'idle', browser: 'idle', connection: 'idle' });
    setErrors({});

    const isProctored    = examMeta?.proctoring_enabled !== false;
    const needsFullscreen = examMeta?.require_fullscreen !== false;

    await checkBrowser();
    await checkConnection();

    if (isProctored) {
      // Only check camera and mic when AI proctoring is enabled
      await checkCamera();
      await checkMicrophone();
    } else {
      // Auto-pass camera and mic for non-proctored exams
      setStatuses(prev => ({ ...prev, camera: 'pass', microphone: 'pass' }));
    }

    if (needsFullscreen) {
      await checkFullscreen();
    } else {
      // Auto-pass fullscreen if exam doesn't require it
      setStatuses(prev => ({ ...prev, fullscreen: 'pass' }));
    }

    setIsRunning(false);
    setIsDone(true);
  }, [checkBrowser, checkConnection, checkCamera, checkMicrophone, checkFullscreen, examMeta]);

  // ── Compute readiness ────────────────────────────────────────────────────────
  const CHECKS = getChecks(examMeta);
  const criticalChecks = CHECKS.filter(c => c.critical && !c.skip).map(c => c.id);
  const allCriticalPassed = criticalChecks.every(id => statuses[id] === 'pass');
  const anyFailed = Object.values(statuses).some(s => s === 'fail');
  const criticalFailed = criticalChecks.filter(id => statuses[id] === 'fail');

  // ── Verify exam password ─────────────────────────────────────────────────────
  const verifyPassword = async () => {
    if (!pwdEntry.trim()) { setPwdError('Please enter the exam password'); return; }
    try {
      await axios.post(`/api/exams/${examId}/verify-password`, { password: pwdEntry });
      setPwdVerified(true);
      setPwdError('');
    } catch (err) {
      setPwdError(err.response?.data?.error || 'Incorrect password. Please try again.');
    }
  };

  // ── Start exam (after all checks pass + password verified) ───────────────────
  const handleStartExam = async () => {
    if (!examId) { toast.error('No exam selected'); return; }
    // Check password if required
    if (pwdRequired && !pwdVerified) { setPwdError('Please verify the exam password first'); return; }
    try {
      // Request fullscreen
      await document.documentElement.requestFullscreen?.();
      // Create attempt
      const res = await axios.post('/api/attempts/start', { exam_id: examId });
      const aid = res.data.id || res.data.attempt_id;
      setAttemptId(aid);
      // Stop camera here; ExamPage will re-acquire it
      if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
      navigate(`/exam/${examId}`, { state: { attemptId: aid } });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start exam');
    }
  };

  const handleRetryCheck = async (checkId) => {
    const retryMap = { camera: checkCamera, microphone: checkMicrophone, fullscreen: checkFullscreen, browser: checkBrowser, connection: checkConnection };
    if (retryMap[checkId]) await retryMap[checkId]();
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
      backgroundImage: 'radial-gradient(ellipse at 30% 40%, rgba(79,142,247,0.04) 0%, transparent 60%), radial-gradient(ellipse at 70% 60%, rgba(124,58,237,0.04) 0%, transparent 60%)',
    }}>
      <div style={{ width: '100%', maxWidth: 780 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '2px', marginBottom: 10 }}>
            ⬡ EXAMAI — PRE-EXAM CHECK
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.5px' }}>
            System & Permission Check
          </h1>
          {examTitle && (
            <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              Before starting: <strong style={{ color: 'var(--accent-primary)' }}>{examTitle}</strong>
            </div>
          )}
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8, maxWidth: 520, margin: '8px auto 0' }}>
            We need to verify your camera, microphone, and browser permissions before the exam begins.
            All required checks must pass to proceed.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: cameraStream ? '1fr 1fr' : '1fr', gap: 24, alignItems: 'start' }}>

          {/* Checks panel */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600 }}>Permission Checks</h2>
              {isDone && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                  background: allCriticalPassed ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                  color: allCriticalPassed ? 'var(--accent-success)' : 'var(--accent-danger)',
                }}>
                  {allCriticalPassed ? '✓ ALL CLEAR' : `${criticalFailed.length} ISSUE${criticalFailed.length > 1 ? 'S' : ''} FOUND`}
                </span>
              )}
            </div>

            {CHECKS.map(check => (
              <CheckRow
                key={check.id}
                check={check}
                status={statuses[check.id]}
                errorMsg={errors[check.id]}
                onRetry={statuses[check.id] === 'fail' ? () => handleRetryCheck(check.id) : null}
              />
            ))}

            {/* Action buttons */}
            <div style={{ marginTop: 20 }}>
              {!isDone && (
                <button
                  className="btn btn-primary w-full btn-lg"
                  onClick={runAllChecks}
                  disabled={isRunning}
                  style={{ justifyContent: 'center' }}
                >
                  {isRunning ? (
                    <><span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>◌</span> Running checks...</>
                  ) : '▶ Run Permission Checks'}
                </button>
              )}

              {isDone && !allCriticalPassed && (
                <div>
                  <div className="alert alert-danger" style={{ marginBottom: 14 }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Cannot start exam</div>
                      <div style={{ fontSize: 12 }}>
                        The following required permissions failed:&nbsp;
                        <strong>{criticalFailed.map(id => CHECKS.find(c => c.id === id)?.label).join(', ')}</strong>.
                        Fix the issues above and retry.
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-secondary w-full" onClick={runAllChecks} style={{ justifyContent: 'center' }}>
                    ↻ Re-run All Checks
                  </button>
                </div>
              )}

              {isDone && allCriticalPassed && (
                <div>
                  <div className="alert alert-success" style={{ marginBottom: 14 }}>
                    <span>✓ All required permissions granted. You are ready to begin.</span>
                  </div>
                  {anyFailed && (
                    <div className="alert alert-warning" style={{ marginBottom: 14, fontSize: 12 }}>
                      ⚠ Some optional checks have warnings, but you may still proceed.
                    </div>
                  )}
                  {/* Exam start password entry */}
                  {pwdRequired && !pwdVerified && (
                    <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(124,58,237,0.08)', borderRadius: 10, border: '1px solid rgba(124,58,237,0.2)' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--accent-secondary)' }}>
                        🔑 Exam Password Required
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                        Enter the password provided by your proctor to start this exam.
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          className="form-input"
                          type="text"
                          placeholder="Enter exam password"
                          value={pwdEntry}
                          onChange={e => { setPwdEntry(e.target.value); setPwdError(''); }}
                          onKeyDown={e => e.key === 'Enter' && verifyPassword()}
                          style={{ flex: 1, fontFamily: 'var(--font-mono)', letterSpacing: 2, fontSize: 16, textTransform: 'uppercase' }}
                          autoFocus
                        />
                        <button className="btn btn-primary" onClick={verifyPassword}>Verify</button>
                      </div>
                      {pwdError && <div style={{ color: 'var(--accent-danger)', fontSize: 12, marginTop: 8 }}>⚠ {pwdError}</div>}
                    </div>
                  )}
                  {pwdRequired && pwdVerified && (
                    <div className="alert alert-success" style={{ marginBottom: 14, fontSize: 12 }}>
                      🔑 Password verified ✓
                    </div>
                  )}
                  <button
                    className="btn btn-primary w-full btn-lg"
                    onClick={handleStartExam}
                    disabled={pwdRequired && !pwdVerified}
                    style={{ justifyContent: 'center', background: (pwdRequired && !pwdVerified) ? undefined : 'linear-gradient(135deg, var(--accent-success), #059669)' }}
                  >
                    🚀 Start Exam Now
                  </button>
                  <button className="btn btn-secondary w-full" onClick={runAllChecks} style={{ justifyContent: 'center', marginTop: 8 }}>
                    ↻ Re-run Checks
                  </button>
                </div>
              )}

              {!isDone && !isRunning && (
                <button className="btn btn-secondary w-full" onClick={() => navigate('/dashboard')} style={{ justifyContent: 'center', marginTop: 10 }}>
                  ← Back to Dashboard
                </button>
              )}
            </div>
          </div>

          {/* Camera preview panel */}
          {cameraStream && (
            <div className="card" style={{ padding: 24 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Camera Preview</h2>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 0 }}>
                Make sure your face is clearly visible and well-lit.
              </p>
              <CameraPreview stream={cameraStream} />

              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>Camera tips:</div>
                {[
                  'Sit in a well-lit room facing the light source',
                  'Your full face must be visible at all times',
                  'No other people should be in the camera frame',
                  'Remove sunglasses or hats that cover your face',
                ].map((tip, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                    <span style={{ color: 'var(--accent-success)', flexShrink: 0 }}>✓</span>
                    {tip}
                  </div>
                ))}
              </div>

              {/* What AI monitors */}
              <div style={{ marginTop: 20, padding: 14, background: 'rgba(79,142,247,0.07)', borderRadius: 8, border: '1px solid rgba(79,142,247,0.15)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-primary)', marginBottom: 8, letterSpacing: '0.5px' }}>
                  🤖 AI MONITORING ACTIVE DURING EXAM
                </div>
                {['Face detection every 5 seconds', 'Multiple faces detection', 'Looking away detection', 'Tab switching & window blur', 'Copy/paste & keyboard shortcuts'].map((item, i) => (
                  <div key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, paddingLeft: 4 }}>• {item}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Help section */}
        {isDone && !allCriticalPassed && (
          <div className="card" style={{ marginTop: 20, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>🛠 How to fix permission issues</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Chrome / Edge</div>
                <div>1. Click the 🔒 lock icon in address bar</div>
                <div>2. Set Camera → Allow</div>
                <div>3. Set Microphone → Allow</div>
                <div>4. Refresh this page</div>
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Firefox</div>
                <div>1. Click the camera icon in address bar</div>
                <div>2. Remove the "Block" permission</div>
                <div>3. Reload and allow when prompted</div>
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Windows Settings</div>
                <div>1. Open Settings → Privacy</div>
                <div>2. Camera → Allow apps to access</div>
                <div>3. Microphone → Allow apps to access</div>
                <div>4. Restart browser</div>
              </div>
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--text-muted)' }}>
          Your camera feed is only active during permission testing and the exam itself.
          It is never recorded or stored.
        </div>
      </div>
    </div>
  );
}
